import React, { useMemo, useState, useCallback, useEffect } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Paginator } from "primereact/paginator";
import { AdvanceFilter } from "../../M_Components/M_Table/advanceFilter/AdvanceFilter";

import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

import TableHeader from "../tableHelpers/TableHeader";
import ColumnMenu from "../tableHelpers/ColumnMenu";
import ViewsMenu from "../tableHelpers/ViewMenu";

import {
  headerCellStyle,
  cellStyle as tdStyle,
} from "../tableHelpers/TableStyleUtils";

import { useTableFilters } from "../tableHelpers/useTableFilters";

import "./TenderSummaryTable.css";

//region Stable defaults

/**
 * Stable default values used instead of defaultProps.
 *
 * Important:
 * - avoids React warning for function components using defaultProps;
 * - avoids recreating empty references during rerenders.
 */
const EMPTY_OBJECT = {};

//endregion

//region Stable column identifiers

/**
 * Stable internal identifiers for column selection.
 *
 * Important:
 * - these values are intentionally NOT translated;
 * - they are used only as stable internal keys for ColumnMenu state;
 * - header text is translated separately.
 *
 * This avoids the bug where translated labels change during rerenders/loading
 * and the selectedColumns state keeps only a random subset of columns.
 */
const COLUMN_KEY = {
  Origin: "Origin",
  Destination: "Destination",
  TotalQuotes: "TotalQuotes",
  TotalBidValue: "TotalBidValue",
  MaxUnitValue: "MaxUnitValue",
  MinUnitValue: "MinUnitValue",
  TotalLoads: "TotalLoads",
  MaxLoads: "MaxLoads",
  MinLoads: "MinLoads",
  MaxETD: "MaxETD",
  MinETD: "MinETD",
  IsSatisfied: "IsSatisfied",
  SatisfiedAt: "SatisfiedAt",
  SatisfiedInPhase: "SatisfiedInPhase",
};

//endregion

//region Lightweight row readers

/**
 * Reads max unit value from one summary row.
 *
 * Priority:
 * - new backend field: maxUnitPrice
 * - defensive fallback: maxBidValue
 *
 * @param {object} row - Summary row.
 * @returns {number|null} Max unit value.
 */
const getRowMaxUnitValue = (row) =>
  row?.maxUnitPrice ??
  row?.MaxUnitPrice ??
  row?.maxBidValue ??
  row?.MaxBidValue ??
  null;

/**
 * Reads min unit value from one summary row.
 *
 * Priority:
 * - new backend field: minUnitPrice
 * - defensive fallback: minBidValue
 *
 * @param {object} row - Summary row.
 * @returns {number|null} Min unit value.
 */
const getRowMinUnitValue = (row) =>
  row?.minUnitPrice ??
  row?.MinUnitPrice ??
  row?.minBidValue ??
  row?.MinBidValue ??
  null;

//endregion

//region Currency helpers

/**
 * Reads the Tender currency value regardless of casing/shape.
 *
 * Important:
 * - in TenderPhase mode, the currency still belongs to the parent Tender;
 * - this component already receives tenderInfo from the parent page.
 *
 * Supported shapes:
 * - tenderInfo.Currency = "$"
 * - tenderInfo.currency = "EUR"
 * - tenderInfo.CurrencyCode = "USD"
 * - tenderInfo.Currency = { Symbol, Code, Name }
 *
 * @param {object} tenderInfo - Tender DTO.
 * @returns {string} Printable currency suffix.
 */
const getTenderCurrencyLabel = (tenderInfo) => {
  const rawCurrency =
    tenderInfo?.Currency ??
    tenderInfo?.currency ??
    tenderInfo?.CurrencySymbol ??
    tenderInfo?.currencySymbol ??
    tenderInfo?.CurrencyCode ??
    tenderInfo?.currencyCode ??
    tenderInfo?.CurrencyName ??
    tenderInfo?.currencyName ??
    "";

  if (rawCurrency && typeof rawCurrency === "object") {
    return String(
      rawCurrency.Symbol ??
      rawCurrency.symbol ??
      rawCurrency.Code ??
      rawCurrency.code ??
      rawCurrency.Name ??
      rawCurrency.name ??
      ""
    ).trim();
  }

  return String(rawCurrency ?? "").trim();
};

/**
 * Formats a numeric value with the current locale.
 *
 * @param {number} value - Numeric value.
 * @param {string|undefined} locale - UI locale.
 * @returns {string} Formatted number.
 */
const formatNumber = (value, locale) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value));

//endregion

/**
 * TenderSummaryTable
 * =================================================================================================
 * Lane-level summary view for a Tender.
 *
 * Responsibilities:
 * - Render summary rows already prepared by the parent page.
 * - Support lightweight client-side filtering, pagination, column visibility and Excel export.
 * - Respect Spot vs Non-Spot column visibility.
 *
 * Non-responsibilities:
 * - Does not compute totals.
 * - Does not merge snapshot data with accepted quote aggregates.
 * - Does not apply Tender business rules.
 * - Does not perform heavy filtering or ordering of large backend collections.
 *
 * Expected row shape from the parent:
 * - laneId
 * - origin
 * - destination
 * - totalQuotes
 * - totalBidValue
 * - maxUnitPrice
 * - minUnitPrice
 * - totalLoads
 * - maxLoads
 * - minLoads
 * - maxEstimatedTransitDays
 * - minEstimatedTransitDays
 * - isSatisfied
 * - satisfiedAt
 * - satisfiedInPhase
 *
 * Backward compatibility:
 * - maxBidValue / minBidValue are still accepted as fallbacks if present.
 */
export default function TenderSummaryTable({
  tenderInfo = EMPTY_OBJECT,
  rows,
  isSpotTender = undefined,
  onRefresh = null,
}) {
  const { t, i18n } = useTranslation();

  //region Pagination state

  const [first, setFirst] = useState(0);
  const [pageRows, setPageRows] = useState(10);

  //endregion

  //region Menu anchors

  const [anchorAF, setAnchorAF] = useState(null);
  const [anchorCols, setAnchorCols] = useState(null);
  const [anchorViews, setAnchorViews] = useState(null);

  //endregion

  //region Views and filters state

  const {
    metaPair,
    views,
    addMetaPair,
    deleteFilter,
    deleteAllFilters,
    submitFilters,
    resetToDefault,
    applyView,
    saveView,
  } = useTableFilters({
    onSubmit: () => setFirst(0),
  });

  //endregion

  //region Formatting helpers

  /**
   * Currency inherited from the Tender parent.
   *
   * Important:
   * - in TenderPhase context, this must still be the parent Tender currency.
   */
  const currencyLabel = useMemo(
    () => getTenderCurrencyLabel(tenderInfo),
    [tenderInfo]
  );

  const currentLocale = i18n?.resolvedLanguage || i18n?.language || undefined;

  /**
   * Safely formats table numeric-like values.
   *
   * @param {any} value - Value to format.
   * @returns {string|number} Dash when nullish, otherwise formatted value.
   */
  const fmt = useCallback(
    (value) => {
      if (value == null) return "-";

      const numericValue = Number(value);

      if (!Number.isFinite(numericValue)) {
        return value;
      }

      return formatNumber(numericValue, currentLocale);
    },
    [currentLocale]
  );

  /**
   * Safely formats currency-like numeric values.
   *
   * Example:
   * - value: 200
   * - currencyLabel: "$"
   * - result: "200 $"
   *
   * @param {any} value - Value to format.
   * @returns {string} Dash when nullish, otherwise formatted value with Tender currency.
   */
  const fmtCurrency = useCallback(
    (value) => {
      if (value == null) return "-";

      const numericValue = Number(value);

      if (!Number.isFinite(numericValue)) {
        return String(value);
      }

      const formatted = formatNumber(numericValue, currentLocale);

      return currencyLabel ? `${formatted} ${currencyLabel}` : formatted;
    },
    [currencyLabel, currentLocale]
  );

  /**
   * Formats satisfiedAt for display.
   *
   * Rules:
   * - null/empty => "-"
   * - "Current" stays translated as current phase/current
   * - valid ISO date => stable yyyy-mm-dd hh:mm:ss output
   * - invalid value => original string
   *
   * @param {string|null|undefined} value - Raw satisfiedAt value.
   * @returns {string} Display value.
   */
  const fmtDate = useCallback(
    (value) => {
      if (!value) return "-";
      if (String(value) === "Current") return t("CurrentPhase") || "Current";

      try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return String(value);

        return d.toISOString().replace("T", " ").replace("Z", "");
      } catch {
        return String(value);
      }
    },
    [t]
  );

  //endregion

  //region Tender type detection

  /**
   * Computes Spot vs Non-Spot mode.
   *
   * Priority:
   * - explicit prop
   * - defensive fields on tenderInfo
   */
  const computedIsSpotTender =
    typeof isSpotTender === "boolean"
      ? isSpotTender
      : !!(
        tenderInfo?.isSpotTender ??
        tenderInfo?.IsSpotTender ??
        tenderInfo?.isSpot ??
        tenderInfo?.IsSpot
      );

  /**
   * Only Non-Spot tenders show the satisfied-in-phase column.
   */
  const shouldRenderSatisfiedInPhaseColumn = !computedIsSpotTender;

  //endregion

  //region Translated header labels

  /**
   * Header labels are translated here, but the actual column selection state
   * does NOT depend on these strings.
   */
  const COL = useMemo(
    () => ({
      Origin: t("Origin") || "Origin",
      Destination: t("Destination") || "Destination",
      TotalQuotes: t("TotalQts") || "Total Quotes",
      TotalBidValue: t("TotalBidValue") || "Total Bid Value",
      MaxUnitValue: t("MaxUnitValue") || "Max Unit Value",
      MinUnitValue: t("MinUnitValue") || "Min Unit Value",
      TotalLoads: t("TotalLoads") || "Total Loads",
      MaxLoads: t("MaxLoads") || "Max Loads",
      MinLoads: t("MinLoads") || "Min Loads",
      MaxETD: t("MaxETD") || "Max ETD",
      MinETD: t("MinETD") || "Min ETD",
      IsSatisfied: t("IsSatisfied") || "Is Satisfied",
      SatisfiedAt: t("SatisfiedAt") || "Satisfied At",
      SatisfiedInPhase: t("Satisfied InPhase") || "Satisfied In Phase",
    }),
    [t]
  );

  //endregion

  //region Advanced filter metadata

  /**
   * Only lightweight textual filters are supported here.
   *
   * This is acceptable on the FE because:
   * - it is small/simple UI filtering;
   * - it does not re-model large backend datasets;
   * - it does not replace server-side data loading rules.
   */
  const metaType = useMemo(
    () => [
      {
        metaName: "origin",
        metaDisplay: COL.Origin,
        metaType: "string",
      },
      {
        metaName: "destination",
        metaDisplay: COL.Destination,
        metaType: "string",
      },
    ],
    [COL.Origin, COL.Destination]
  );

  //endregion

  //region Lightweight client-side filtering

  /**
   * Applies only simple Origin / Destination filters on the already prepared rows.
   *
   * Important:
   * - no sorting is applied here;
   * - no heavy collection transformations are applied here;
   * - parent remains responsible for the real backend-driven dataset.
   */
  const filteredRows = useMemo(() => {
    let data = Array.isArray(rows) ? [...rows] : [];

    for (const filter of metaPair) {
      const rawValue = filter?.value;
      if (!rawValue) continue;

      const value = String(rawValue).toLowerCase();
      const operation = filter?.operation || "Contains";

      if (filter.name === "origin") {
        data = data.filter((row) => {
          const cellValue = String(row?.origin ?? "").toLowerCase();

          if (operation === "Equals") return cellValue === value;
          return cellValue.includes(value);
        });
      }

      if (filter.name === "destination") {
        data = data.filter((row) => {
          const cellValue = String(row?.destination ?? "").toLowerCase();

          if (operation === "Equals") return cellValue === value;
          return cellValue.includes(value);
        });
      }
    }

    return data;
  }, [rows, metaPair]);

  //endregion

  //region Pagination slice

  const pagedRows = useMemo(
    () => filteredRows.slice(first, first + pageRows),
    [filteredRows, first, pageRows]
  );

  //endregion

  //region Visible columns

  /**
   * Stable internal column list for this table.
   *
   * Important:
   * - these values never change with translations/loading;
   * - that prevents selectedColumns from collapsing unexpectedly.
   */
  const singleColumns = useMemo(() => {
    const base = [
      COLUMN_KEY.Origin,
      COLUMN_KEY.Destination,
      COLUMN_KEY.TotalQuotes,
      COLUMN_KEY.TotalBidValue,
      COLUMN_KEY.MaxUnitValue,
      COLUMN_KEY.MinUnitValue,
      COLUMN_KEY.TotalLoads,
      COLUMN_KEY.MaxLoads,
      COLUMN_KEY.MinLoads,
      COLUMN_KEY.MaxETD,
      COLUMN_KEY.MinETD,
      COLUMN_KEY.IsSatisfied,
      COLUMN_KEY.SatisfiedAt,
    ];

    if (!shouldRenderSatisfiedInPhaseColumn) return base;

    return [...base, COLUMN_KEY.SatisfiedInPhase];
  }, [shouldRenderSatisfiedInPhaseColumn]);

  /**
   * Selected columns use stable identifiers, not translated labels.
   */
  const [selectedColumns, setSelectedColumns] = useState(() => singleColumns);

  /**
   * Keeps selected columns valid if tender type changes while mounted.
   *
   * Important:
   * - because identifiers are stable now, translations/loading no longer break this state.
   */
  useEffect(() => {
    setSelectedColumns((prev) => {
      const allowed = new Set(singleColumns);
      const next = (prev || []).filter((columnKey) => allowed.has(columnKey));
      return next.length ? next : singleColumns;
    });
  }, [singleColumns]);

  //endregion

  //region Excel export

  /**
   * Exports the currently filtered dataset using the currently visible columns.
   *
   * The export respects:
   * - simple FE filters already applied to the rendered table
   * - current selected columns
   * - Spot vs Non-Spot column visibility
   */
  const exportExcel = useCallback(async () => {
    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet("TenderSummary");

    const colDefs = [
      {
        key: COLUMN_KEY.Origin,
        header: COL.Origin,
        get: (row) => row?.origin ?? "-",
      },
      {
        key: COLUMN_KEY.Destination,
        header: COL.Destination,
        get: (row) => row?.destination ?? "-",
      },
      {
        key: COLUMN_KEY.TotalQuotes,
        header: COL.TotalQuotes,
        get: (row) => fmt(row?.totalQuotes),
      },
      {
        key: COLUMN_KEY.TotalBidValue,
        header: COL.TotalBidValue,
        get: (row) => fmtCurrency(row?.totalBidValue),
      },
      {
        key: COLUMN_KEY.MaxUnitValue,
        header: COL.MaxUnitValue,
        get: (row) => fmtCurrency(getRowMaxUnitValue(row)),
      },
      {
        key: COLUMN_KEY.MinUnitValue,
        header: COL.MinUnitValue,
        get: (row) => fmtCurrency(getRowMinUnitValue(row)),
      },
      {
        key: COLUMN_KEY.TotalLoads,
        header: COL.TotalLoads,
        get: (row) => fmt(row?.totalLoads),
      },
      {
        key: COLUMN_KEY.MaxLoads,
        header: COL.MaxLoads,
        get: (row) => fmt(row?.maxLoads),
      },
      {
        key: COLUMN_KEY.MinLoads,
        header: COL.MinLoads,
        get: (row) => fmt(row?.minLoads),
      },
      {
        key: COLUMN_KEY.MaxETD,
        header: COL.MaxETD,
        get: (row) => fmt(row?.maxEstimatedTransitDays),
      },
      {
        key: COLUMN_KEY.MinETD,
        header: COL.MinETD,
        get: (row) => fmt(row?.minEstimatedTransitDays),
      },
      {
        key: COLUMN_KEY.IsSatisfied,
        header: COL.IsSatisfied,
        get: (row) =>
          row?.isSatisfied == null ? "-" : row.isSatisfied ? "Yes" : "No",
      },
      {
        key: COLUMN_KEY.SatisfiedAt,
        header: COL.SatisfiedAt,
        get: (row) => fmtDate(row?.satisfiedAt),
      },
    ];

    if (shouldRenderSatisfiedInPhaseColumn) {
      colDefs.push({
        key: COLUMN_KEY.SatisfiedInPhase,
        header: COL.SatisfiedInPhase,
        get: (row) => row?.satisfiedInPhase ?? "-",
      });
    }

    const exportCols = colDefs.filter((col) =>
      selectedColumns.includes(col.key)
    );

    sheet.addRow(exportCols.map((col) => col.header));

    (filteredRows || []).forEach((row) => {
      sheet.addRow(exportCols.map((col) => col.get(row)));
    });

    try {
      sheet.getRow(1).font = { bold: true };
    } catch {
      // No action is required when styling fails.
    }

    const buffer = await workbook.xlsx.writeBuffer();

    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      "TenderSummary.xlsx"
    );
  }, [
    COL,
    shouldRenderSatisfiedInPhaseColumn,
    filteredRows,
    fmt,
    fmtCurrency,
    fmtDate,
    selectedColumns,
  ]);

  //endregion

  //region Render

  return (
    <div className="tenderSummary-wrapper">
      <TableHeader
        filterCount={metaPair.length}
        onRefresh={onRefresh}
        onExport={exportExcel}
        onOpenViews={(e) => setAnchorViews(e.currentTarget)}
        onOpenFilters={(e) => setAnchorAF(e.currentTarget)}
        onOpenColumns={(e) => setAnchorCols(e.currentTarget)}
      />

      <ColumnMenu
        anchorEl={anchorCols}
        open={!!anchorCols}
        onClose={() => setAnchorCols(null)}
        singleColumns={singleColumns}
        selectedColumns={selectedColumns}
        onChangeSelected={setSelectedColumns}
        columnGroups={[]}
      />

      <ViewsMenu
        anchorEl={anchorViews}
        open={!!anchorViews}
        onClose={() => setAnchorViews(null)}
        viewsMap={views}
        onSelectDefault={() => {
          resetToDefault();
          setAnchorViews(null);
        }}
        onSelectView={(view) => {
          applyView(view);
          setAnchorViews(null);
        }}
        onCreateNewViewClick={() => {
          setAnchorViews(null);
          setAnchorAF(document.getElementById("advFilter"));
        }}
      />

      <AdvanceFilter
        metaPair={metaPair}
        metaType={metaType}
        onAddMetaPair={addMetaPair}
        onSubmitFilters={submitFilters}
        onDeleteFilter={deleteFilter}
        onDeleteAllFilters={deleteAllFilters}
        anchorAF={anchorAF}
        setAnchorAF={setAnchorAF}
        onSaveFilter={(viewName) => saveView(viewName)}
        views={views}
        onToasts={() => { }}
      />

      <div className="tenderSummary-table-container">
        <table className="tenderSummary">
          <thead>
            <tr>
              {selectedColumns.includes(COLUMN_KEY.Origin) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-origin"
                >
                  {COL.Origin}
                </th>
              )}

              {selectedColumns.includes(COLUMN_KEY.Destination) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-destination"
                >
                  {COL.Destination}
                </th>
              )}

              {selectedColumns.includes(COLUMN_KEY.TotalQuotes) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.TotalQuotes}
                </th>
              )}

              {selectedColumns.includes(COLUMN_KEY.TotalBidValue) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.TotalBidValue}
                </th>
              )}

              {selectedColumns.includes(COLUMN_KEY.MaxUnitValue) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.MaxUnitValue}
                </th>
              )}

              {selectedColumns.includes(COLUMN_KEY.MinUnitValue) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.MinUnitValue}
                </th>
              )}

              {selectedColumns.includes(COLUMN_KEY.TotalLoads) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.TotalLoads}
                </th>
              )}

              {selectedColumns.includes(COLUMN_KEY.MaxLoads) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.MaxLoads}
                </th>
              )}

              {selectedColumns.includes(COLUMN_KEY.MinLoads) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.MinLoads}
                </th>
              )}

              {selectedColumns.includes(COLUMN_KEY.MaxETD) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.MaxETD}
                </th>
              )}

              {selectedColumns.includes(COLUMN_KEY.MinETD) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-num"
                >
                  {COL.MinETD}
                </th>
              )}

              {selectedColumns.includes(COLUMN_KEY.IsSatisfied) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-satisfied"
                >
                  {COL.IsSatisfied}
                </th>
              )}

              {selectedColumns.includes(COLUMN_KEY.SatisfiedAt) && (
                <th
                  style={headerCellStyle()}
                  className="tenderSummary-header-cell col-date"
                >
                  {COL.SatisfiedAt}
                </th>
              )}

              {shouldRenderSatisfiedInPhaseColumn &&
                selectedColumns.includes(COLUMN_KEY.SatisfiedInPhase) && (
                  <th
                    style={headerCellStyle()}
                    className="tenderSummary-header-cell col-phase"
                  >
                    {COL.SatisfiedInPhase}
                  </th>
                )}
            </tr>
          </thead>

          <tbody>
            {(pagedRows || []).map((row) => (
              <tr key={`sum-lane-${row.laneId}`}>
                {selectedColumns.includes(COLUMN_KEY.Origin) && (
                  <td
                    className="tenderSummary-cell col-origin"
                    style={tdStyle({
                      fontWeight: "bold",
                    })}
                  >
                    {row.origin ?? "-"}
                  </td>
                )}

                {selectedColumns.includes(COLUMN_KEY.Destination) && (
                  <td
                    className="tenderSummary-cell col-destination"
                    style={tdStyle({
                      fontWeight: "bold",
                    })}
                  >
                    {row.destination ?? "-"}
                  </td>
                )}

                {selectedColumns.includes(COLUMN_KEY.TotalQuotes) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmt(row.totalQuotes)}
                  </td>
                )}

                {selectedColumns.includes(COLUMN_KEY.TotalBidValue) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmtCurrency(row.totalBidValue)}
                  </td>
                )}

                {selectedColumns.includes(COLUMN_KEY.MaxUnitValue) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmtCurrency(getRowMaxUnitValue(row))}
                  </td>
                )}

                {selectedColumns.includes(COLUMN_KEY.MinUnitValue) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmtCurrency(getRowMinUnitValue(row))}
                  </td>
                )}

                {selectedColumns.includes(COLUMN_KEY.TotalLoads) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmt(row.totalLoads)}
                  </td>
                )}

                {selectedColumns.includes(COLUMN_KEY.MaxLoads) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmt(row.maxLoads)}
                  </td>
                )}

                {selectedColumns.includes(COLUMN_KEY.MinLoads) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmt(row.minLoads)}
                  </td>
                )}

                {selectedColumns.includes(COLUMN_KEY.MaxETD) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmt(row.maxEstimatedTransitDays)}
                  </td>
                )}

                {selectedColumns.includes(COLUMN_KEY.MinETD) && (
                  <td className="tenderSummary-cell col-num" style={tdStyle()}>
                    {fmt(row.minEstimatedTransitDays)}
                  </td>
                )}

                {selectedColumns.includes(COLUMN_KEY.IsSatisfied) && (
                  <td
                    className="tenderSummary-cell col-satisfied"
                    style={tdStyle()}
                  >
                    {row.isSatisfied == null
                      ? "-"
                      : row.isSatisfied
                        ? t("Yes")
                        : t("No")}
                  </td>
                )}

                {selectedColumns.includes(COLUMN_KEY.SatisfiedAt) && (
                  <td
                    className="tenderSummary-cell col-date"
                    style={tdStyle()}
                  >
                    {fmtDate(row.satisfiedAt)}
                  </td>
                )}

                {shouldRenderSatisfiedInPhaseColumn &&
                  selectedColumns.includes(COLUMN_KEY.SatisfiedInPhase) && (
                    <td
                      className="tenderSummary-cell col-phase"
                      style={tdStyle()}
                    >
                      {row.satisfiedInPhase ?? "-"}
                    </td>
                  )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Paginator
        first={first}
        rows={pageRows}
        totalRecords={filteredRows.length}
        rowsPerPageOptions={[10, 20, 50]}
        onPageChange={(e) => {
          setFirst(e.first);
          setPageRows(e.rows);
        }}
        template="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
      />
    </div>
  );
  //endregion
}

TenderSummaryTable.propTypes = {
  tenderInfo: PropTypes.object,

  /**
   * Parent-prepared summary rows.
   *
   * Expected to already respect the correct backend context/rules.
   */
  rows: PropTypes.array.isRequired,

  /**
   * Optional explicit spot flag.
   * When omitted, the component tries to infer it from tenderInfo.
   */
  isSpotTender: PropTypes.bool,

  onRefresh: PropTypes.func,
};